import { Button } from "@/lib/components/Button/Button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/lib/components/Popover";
import { NumberInput } from "@/lib/components/NumberInput";
import { Switch } from "@/lib/components/Switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import WavesurferPlayer from "@wavesurfer/react";
import {
  IconKeyboard,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-react";
import { createPortal } from "react-dom";
import { useRef } from "react";
import {
  CUTTING_POINT_GAP_ERROR_MESSAGE,
  formatMs,
  MAX_BPM,
  MAX_CUTTING_POINT_GAP_MS,
  MAX_ZOOM,
  MIN_BPM,
  MIN_ZOOM,
  SHORTCUTS,
  TRASH_CURSOR,
  validateCuttingPointGaps,
} from "./cutting-points-utils";
import {
  useAltKeyPressed,
  useCuttingPointsEditor,
  useWaveformWidth,
} from "./cutting-points-hooks";
import { cn } from "@/lib/utils";

export default function CuttingPointsView({
  trackSrc,
  trackName,
  cuttingPoints,
  onChangeCuttingPoints,
  bpm,
  onChangeBpm,
  gridOffsetMs,
  onChangeGridOffsetMs,
  onBack,
  onNext,
}: {
  trackSrc: string | undefined;
  trackName: string | undefined;
  cuttingPoints: number[];
  onChangeCuttingPoints: (points: number[]) => void;
  bpm: number;
  onChangeBpm: (bpm: number) => void;
  gridOffsetMs: number;
  onChangeGridOffsetMs: (ms: number) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  // View-only state for the "generate cutting points from clip durations"
  // confirmation dialog. All waveform interaction, playback and grid state
  // lives in the useCuttingPointsEditor hook.
  const isAltPressed = useAltKeyPressed();

  const {
    containerRef,
    scrollContainerRef,
    waveformRef,
    isLoading,
    songDurationMs,
    currentTimeMs,
    isPlaying,
    zoom,
    enableGrid,
    setEnableGrid,
    snapToGrid,
    setSnapToGrid,
    showGapLabels,
    setShowGapLabels,
    selectedPoints,
    marquee,
    barHit,
    dragTooltip,
    isAnalyzing,
    beatGridLines,
    wavesurferHandlers,
    handleWaveformClick,
    handleWaveformDoubleClick,
    handleWaveformPointerDown,
    handlePlayheadPointerDown,
    makeHandlePointerDown,
    handleRemove,
    togglePlayback,
    zoomIn,
    zoomOut,
    analyzeTrack,
  } = useCuttingPointsEditor({
    cuttingPoints,
    onChangeCuttingPoints,
    bpm,
    onChangeBpm,
    gridOffsetMs,
    onChangeGridOffsetMs,
  });

  const hasGapError = !validateCuttingPointGaps(cuttingPoints);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Cutting points</DialogTitle>
        <DialogDescription>
          Click to move the playhead, double-click to add a cutting point, and
          drag handles to fine-tune.
        </DialogDescription>
      </DialogHeader>
      <div>
        <div className="mb-1 flex min-w-0 items-end justify-between">
          <span className="mb-1.5 truncate text-sm font-semibold text-text-primary">
            {trackName}
          </span>
          <ShortcutsTooltip />
        </div>
        <div
          className="relative rounded-md border border-grey-200 px-3"
          ref={containerRef}
        >
          {isLoading && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="iconSm"
              disabled={isLoading}
              onClick={togglePlayback}
              aria-label={isPlaying ? "Pause track" : "Play track"}
              className="z-100 shrink-0"
            >
              {isPlaying ? <IconPlayerPauseFilled /> : <IconPlayerPlayFilled />}
            </Button>
            <div
              ref={scrollContainerRef}
              className="relative flex-1 overflow-x-auto px-1 py-6"
              onPointerDown={handleWaveformPointerDown}
            >
              <div
                ref={waveformRef}
                className="relative touch-none select-none"
                style={{ width: `${zoom * 100}%` }}
                onClick={handleWaveformClick}
                onDoubleClick={handleWaveformDoubleClick}
              >
                {/* Beat grid based on the BPM, drawn as small ticks along the
                    top edge so they don't cover the waveform. */}
                {!isLoading && songDurationMs !== null && enableGrid && (
                  <BeatGrid lines={beatGridLines} snapToGrid={snapToGrid} />
                )}

                {trackSrc && (
                  <WavesurferPlayer
                    key={trackSrc}
                    height={96}
                    waveColor="#e5e7eb"
                    progressColor="#cdd0d5"
                    url={trackSrc}
                    backend="WebAudio"
                    {...wavesurferHandlers}
                    barWidth={1.5}
                    barGap={3}
                    barRadius={3}
                    cursorWidth={0}
                    normalize={true}
                    interact={false}
                  />
                )}

                {!isLoading &&
                  songDurationMs !== null &&
                  cuttingPoints.map((point, index) => (
                    <CuttingPointHandle
                      key={index}
                      positionPct={(point / songDurationMs) * 100}
                      altPressed={isAltPressed}
                      isSelected={selectedPoints.has(point)}
                      onPointerDown={makeHandlePointerDown(index)}
                      onRemove={() => handleRemove(index)}
                    />
                  ))}

                {!isLoading && songDurationMs !== null && showGapLabels && (
                  <GapLabels
                    cuttingPoints={cuttingPoints}
                    songDurationMs={songDurationMs}
                  />
                )}

                {marquee && <Marquee marquee={marquee} />}

                {!isLoading && songDurationMs !== null && barHit && (
                  <BarHitPulse
                    barHit={barHit}
                    songDurationMs={songDurationMs}
                  />
                )}

                {/* Custom playhead, drawn behind the handles. The wider area
                    is draggable to scrub with a looping preview. */}
                {!isLoading && songDurationMs !== null && (
                  <Playhead
                    currentTimeMs={currentTimeMs}
                    songDurationMs={songDurationMs}
                    onPointerDown={handlePlayheadPointerDown}
                  />
                )}
              </div>
            </div>
            <ZoomControls
              zoom={zoom}
              isLoading={isLoading}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
            />
          </div>
        </div>
        <div className="mt-2 flex items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            <div className="text-sm text-text-secondary tabular-nums">
              {formatMs(currentTimeMs)} / {formatMs(songDurationMs ?? 0)}
            </div>
            <p
              className={cn(
                "text-sm",
                hasGapError ? "text-error" : "opacity-0",
              )}
            >
              {CUTTING_POINT_GAP_ERROR_MESSAGE}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-medium text-text-primary">
              Grid Labels
              <Switch
                checked={showGapLabels}
                onCheckedChange={setShowGapLabels}
              />
            </label>
            <GridSettingsPopover
              isLoading={isLoading}
              enableGrid={enableGrid}
              onEnableGridChange={setEnableGrid}
              bpm={bpm}
              onBpmChange={onChangeBpm}
              gridOffsetMs={gridOffsetMs}
              onGridOffsetChange={onChangeGridOffsetMs}
              snapToGrid={snapToGrid}
              onSnapToGridChange={setSnapToGrid}
              isAnalyzing={isAnalyzing}
              onAnalyzeTrack={analyzeTrack}
            />
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          disabled={cuttingPoints.length === 0}
          onClick={onNext}
        >
          Next
        </Button>
      </DialogFooter>

      <DragTooltipPortal dragTooltip={dragTooltip} />
    </>
  );
}

function ShortcutsTooltip() {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          icon={<IconKeyboard />}
          iconPosition="leading"
          className="mt-1 self-start"
        >
          Shortcuts
        </Button>
      </TooltipTrigger>
      <TooltipContent
        variant="secondary"
        size="lg"
        side="left"
        align="start"
        className="max-h-(--radix-tooltip-content-available-height) min-w-[320px] overflow-y-auto"
      >
        <ul className="flex flex-col gap-1.5">
          {SHORTCUTS.map(({ keys, action }) => (
            <li
              key={keys}
              className="flex items-center justify-between gap-4 text-xs"
            >
              <span className="text-text-secondary">{action}</span>
              <kbd className="bg-grey-50 shrink-0 rounded border border-grey-200 px-1.5 py-0.5 font-medium whitespace-nowrap text-text-primary tabular-nums">
                {keys}
              </kbd>
            </li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}

function BeatGrid({
  lines,
  snapToGrid,
}: {
  lines: { pct: number; isBar: boolean }[];
  snapToGrid: boolean;
}) {
  return (
    <div className="pointer-events-none absolute inset-x-0 -top-6 z-10">
      {lines.map(({ pct, isBar }, i) => (
        <div
          key={i}
          className={`absolute top-0 w-px -translate-x-1/2 rounded-b-xl ${
            snapToGrid ? "bg-secondary-400" : "bg-grey-400/60"
          } ${isBar ? "h-2" : "h-1"}`}
          style={{ left: `${pct}%` }}
        />
      ))}
    </div>
  );
}

function GapLabels({
  cuttingPoints,
  songDurationMs,
}: {
  cuttingPoints: number[];
  songDurationMs: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widthPx = useWaveformWidth(containerRef);
  const sorted = [...cuttingPoints].sort((a, b) => a - b);

  const CHAR_PX = 6;
  const PADDING_PX = 4;

  const labels: {
    midMs: number;
    text: string;
    show: boolean;
    isOverLimit: boolean;
  }[] = [];
  let lastRightPx = -Infinity;
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]!;
    const point = sorted[i]!;
    const midMs = (prev + point) / 2;
    const gapMs = point - prev;
    const text = `${(gapMs / 1000).toFixed(2)}s`;
    const isOverLimit = gapMs > MAX_CUTTING_POINT_GAP_MS;
    const centerPx = (midMs / songDurationMs) * widthPx;
    const halfWidthPx = (text.length * CHAR_PX + PADDING_PX) / 2;
    const show = widthPx === 0 || centerPx - halfWidthPx >= lastRightPx;
    if (show && widthPx > 0) lastRightPx = centerPx + halfWidthPx;
    labels.push({ midMs, text, show, isOverLimit });
  }

  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-x-0 -bottom-2 z-10"
    >
      {labels.map(({ midMs, text, show, isOverLimit }, i) =>
        show ? (
          <span
            key={i}
            className={cn(
              "absolute top-0 -translate-x-1/2 text-[10px] leading-none font-medium tabular-nums",
              isOverLimit ? "text-error" : "text-text-secondary",
            )}
            style={{ left: `${(midMs / songDurationMs) * 100}%` }}
          >
            {text}
          </span>
        ) : null,
      )}
    </div>
  );
}

function Marquee({
  marquee,
}: {
  marquee: { leftPct: number; widthPct: number };
}) {
  return (
    <div
      className="pointer-events-none absolute inset-y-0 z-10 bg-blue-400/20 outline outline-blue-400"
      style={{
        left: `${marquee.leftPct}%`,
        width: `${marquee.widthPct}%`,
      }}
    />
  );
}

function BarHitPulse({
  barHit,
  songDurationMs,
}: {
  barHit: { posMs: number; nonce: number };
  songDurationMs: number;
}) {
  return (
    <div className="pointer-events-none absolute inset-0 z-10">
      <div
        key={barHit.nonce}
        className="absolute inset-y-0 w-0.5 -translate-x-1/2"
        style={{
          left: `${(barHit.posMs / songDurationMs) * 100}%`,
        }}
      >
        <div className="absolute inset-y-4 left-1/2 w-0.75 -translate-x-1/2 animate-ping rounded-full bg-tertiary-400 animation-duration-[0.4s] fill-mode-[forwards] repeat-[1]" />
      </div>
    </div>
  );
}

function Playhead({
  currentTimeMs,
  songDurationMs,
  onPointerDown,
}: {
  currentTimeMs: number;
  songDurationMs: number;
  onPointerDown: (e: React.PointerEvent) => void;
}) {
  return (
    <div
      role="slider"
      aria-label="Playhead"
      onPointerDown={onPointerDown}
      onClick={(e) => e.stopPropagation()}
      className="group absolute inset-y-0 z-10 w-4 -translate-x-1/2 cursor-ew-resize"
      style={{
        left: `${(currentTimeMs / songDurationMs) * 100}%`,
      }}
    >
      <div
        className="pointer-events-none absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2"
        style={{ backgroundColor: "#f66c97" }}
      />
    </div>
  );
}

function ZoomControls({
  zoom,
  isLoading,
  onZoomIn,
  onZoomOut,
}: {
  zoom: number;
  isLoading: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
}) {
  return (
    <div className="z-100 flex shrink-0 flex-col gap-1">
      <Button
        type="button"
        variant="outline"
        size="iconSm"
        disabled={isLoading || zoom >= MAX_ZOOM}
        onClick={onZoomIn}
        aria-label="Zoom in"
      >
        <IconZoomIn />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="iconSm"
        disabled={isLoading || zoom <= MIN_ZOOM}
        onClick={onZoomOut}
        aria-label="Zoom out"
      >
        <IconZoomOut />
      </Button>
    </div>
  );
}

function GridSettingsPopover({
  isLoading,
  enableGrid,
  onEnableGridChange,
  bpm,
  onBpmChange,
  gridOffsetMs,
  onGridOffsetChange,
  snapToGrid,
  onSnapToGridChange,
  isAnalyzing,
  onAnalyzeTrack,
}: {
  isLoading: boolean;
  enableGrid: boolean;
  onEnableGridChange: (value: boolean) => void;
  bpm: number;
  onBpmChange: (value: number) => void;
  gridOffsetMs: number;
  onGridOffsetChange: (value: number) => void;
  snapToGrid: boolean;
  onSnapToGridChange: (value: boolean) => void;
  isAnalyzing: boolean;
  onAnalyzeTrack: () => void;
}) {
  return (
    <Popover
      onOpenChange={(open) => {
        // Opening the settings turns the grid on so its controls apply.
        if (open) onEnableGridChange(true);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="xs"
          iconPosition="leading"
          disabled={isLoading}
        >
          {enableGrid ? `${bpm} BPM` : "Show Grid"}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56">
        <div className="flex flex-col">
          <div className="-mx-4 flex items-center justify-between gap-2 border-b border-grey-200 px-4 pb-4">
            <span className="text-sm font-medium text-text-primary">
              Show grid
            </span>
            <Switch checked={enableGrid} onCheckedChange={onEnableGridChange} />
          </div>
          <div className="relative py-1 pt-3">
            {!enableGrid && (
              <div className="absolute inset-0 z-10 cursor-not-allowed rounded-md bg-white/60" />
            )}
            <div
              className={`flex flex-col gap-3 ${
                enableGrid ? "" : "pointer-events-none opacity-50"
              }`}
            >
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-text-primary">
                  Grid BPM
                </span>
                <NumberInput
                  containerSize="sm"
                  min={MIN_BPM}
                  max={MAX_BPM}
                  value={bpm}
                  onValueChange={onBpmChange}
                />
                <p className="text-xs text-text-secondary">
                  Sets the spacing of the grid lines behind the waveform.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-text-primary">
                  Grid offset (ms)
                </span>
                <NumberInput
                  containerSize="sm"
                  min={0}
                  value={Math.round(gridOffsetMs)}
                  onValueChange={onGridOffsetChange}
                />
                <p className="text-xs text-text-secondary">
                  Shifts the grid. Press Cmd/Ctrl+G to align it to the playhead.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-text-primary">
                    Snap to grid
                  </span>
                  <Switch
                    checked={snapToGrid}
                    onCheckedChange={onSnapToGridChange}
                  />
                </div>
                <p className="text-xs text-text-secondary">
                  Snaps the playhead and cutting points to the nearest beat
                  while dragging.
                </p>
              </div>
              <Button
                type="button"
                variant="tertiarySecondary"
                size="sm"
                className="mt-2 border border-tertiary-400"
                isLoading={isAnalyzing}
                onClick={onAnalyzeTrack}
              >
                Analyze track
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DragTooltipPortal({
  dragTooltip,
}: {
  dragTooltip: { left: number; top: number; label: string } | null;
}) {
  if (!dragTooltip) return null;

  return createPortal(
    <div
      className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full rounded bg-black/70 px-1.5 py-0.5 text-xs font-medium whitespace-nowrap text-white tabular-nums"
      style={{ left: dragTooltip.left, top: dragTooltip.top - 4 }}
    >
      {dragTooltip.label}
    </div>,
    document.body,
  );
}

function CuttingPointHandle({
  positionPct,
  altPressed,
  isSelected,
  onPointerDown,
  onRemove,
}: {
  positionPct: number;
  altPressed: boolean;
  isSelected: boolean;
  onPointerDown: (e: React.PointerEvent) => void;
  onRemove: () => void;
}) {
  return (
    <div
      role="slider"
      aria-label="Cutting point"
      onPointerDown={onPointerDown}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
      className="group absolute inset-y-0 z-20 w-4 -translate-x-1/2 cursor-ew-resize"
      style={{
        left: `${positionPct}%`,
        cursor: altPressed ? TRASH_CURSOR : undefined,
      }}
    >
      <div
        className={`absolute inset-y-0 left-1/2 w-0.75 -translate-x-1/2 rounded-full ${
          isSelected ? "bg-blue-400" : "bg-tertiary-400"
        }`}
      />
    </div>
  );
}
